<template>
  <section name="presentation-theme" class="relative p-4 flex flex-col gap-4 pt-18" v-aha-emit-action.view>
    <section v-if="canCreateTheme" class="flex flex-col gap-4">
      <section class="flex justify-between items-center">
        <label class="label-option font-bold">{{ $i18nPresenter.t('My Themes') }}</label>
        <aha-antd-button
          variant="secondary"
          @click="isShowConfigModal = true"
          size="md"
          class="inline-flex items-center gap-2"
        >
          <aha-icon
            name="plus-new"
            width="16"
            height="16"
            viewBox="0 0 16 16"
          />
          {{ $i18nPresenter.t('Create') }}
        </aha-antd-button>
      </section>
      <section class="grid grid-cols-2 gap-4 themes">
        <aha-thumbnail-holder
          name="theme"
          v-aha-emit-action="{ themeId: theme?.id, themeSection: 'myTheme', themeName: theme?.themeName }"
          v-for="theme in themeList"
          :key="theme.id"
          @click="handleSelectTheme(theme)"
          class="relative flex flex-col gap-2"
          :isActive="theme.id == currentPresentationTheme?.themeId"
          v-hover-intent="{
            onEnter: () => previewTheme(theme),
            onExit: () => removePreview(theme)
          }"
        >
          <template #thumbnail-content>
            <ThemeThumbnail
              :theme="theme"
              :showDefaultLogo="!isWhiteLabel"
            >
            </ThemeThumbnail>
          </template>
          <template #context-menu-items>
            <aha-context-menu-item
              v-if="theme.isShareWithTeam"
              @click="toggleShareThemeModal(theme, false)"
              name="UnshareThemeButton"
              v-aha-emit-action="{ presentationThemeId: theme.id }"
            >
              <aha-icon name="share-new" />
              <div>{{ $i18nPresenter.t('Unshare') }} </div>
            </aha-context-menu-item>
            <aha-context-menu-item
              v-else
              @click="toggleShareThemeModal(theme, true)"
              name="ShareThemeButton"
              v-aha-emit-action="{ presentationThemeId: theme.id }"
            >
              <aha-icon name="share-new" />
              <div>{{ $i18nPresenter.t('Share') }}</div>
            </aha-context-menu-item>
            <div class="divider"></div>
            <aha-context-menu-item
              name="deleteThemeButton"
              v-aha-emit-action="{ themeId: theme?.id }"
              @click="showDeleteThemeModal(theme)"
              class="text-danger"
            >
              <aha-icon
                width="16"
                height="16"
                viewBox="0 0 24 24"
                name="trash-new"
              />
              <div>{{ $i18nPresenter.t('Delete') }}</div>
            </aha-context-menu-item>
          </template>
        </aha-thumbnail-holder>
      </section>
    </section>
    <div v-if="showSectionTeamThemes" class="divider bg-secondary-20 h-px"></div>
    <section v-if="showSectionTeamThemes" class="flex flex-col gap-4">
      <label class="label-option font-bold">{{ currentAccount.name }} {{ $i18nPresenter.t('Themes') }}</label>
      <section class="grid grid-cols-2 gap-4 themes">
        <aha-thumbnail-holder
          name="theme"
          v-aha-emit-action="{ themeId: theme?.id, themeSection: 'teamTheme', themeName: theme?.themeName }"
          v-for="theme in teamThemes"
          :key="theme.id"
          @click="handleSelectTheme(theme)"
          class="relative flex flex-col gap-2"
          :showContextMenu="false"
          :isActive="theme.id == currentPresentationTheme?.themeId"
          v-hover-intent="{
            onEnter: () => previewTheme(theme),
            onExit: () => removePreview(theme)
          }"
        >
          <template #thumbnail-content>
            <ThemeThumbnail
              :theme="theme"
              :showDefaultLogo="!isWhiteLabel"
            >
            </ThemeThumbnail>
          </template>
          <template #context-menu-items>
            <aha-context-menu-item @click="showDeleteThemeModal(theme)">
              <div>{{ $i18nPresenter.t('Delete') }}</div>
            </aha-context-menu-item>
          </template>
        </aha-thumbnail-holder>
      </section>
    </section>
    <div v-if="canCreateTheme" class="divider bg-secondary-20 h-px"></div>
    <section class="flex flex-col gap-4">
      <label class="label-option font-bold">{{ $i18nPresenter.t('Official Themes') }}</label>
      <section class="grid grid-cols-2 gap-4 themes">
        <aha-thumbnail-holder
          name="theme"
          v-aha-emit-action="{ themeId: theme?.id, themeSection: 'officialTheme', themeName: theme?.themeName }"
          v-for="theme in officialThemes"
          :key="theme.id"
          @click="handleSelectTheme(theme)"
          class="relative flex flex-col gap-2"
          :showContextMenu="false"
          :isActive="theme.id == currentPresentationTheme?.themeId"
          v-hover-intent="{
            onEnter: () => previewTheme(theme),
            onExit: () => removePreview(theme)
          }"
        >
          <template #thumbnail-content>
            <ThemeThumbnail
              :theme="theme"
              :showDefaultLogo="!isWhiteLabel"
            >
            </ThemeThumbnail>
          </template>
          <template #context-menu-items>
            <aha-context-menu-item @click="showDeleteThemeModal(theme)">
              <div>{{ $i18nPresenter.t('Delete') }}</div>
            </aha-context-menu-item>
          </template>
        </aha-thumbnail-holder>
      </section>
      <aha-modal
        v-if="isShowConfigModal"
        width="1150px"
        :title="$i18nPresenter.t('Create Theme')"
        noMarginPadding
        @hide="isShowConfigModal = false"
      >
        <ThemeConfig
          :colorPalette="presetColorPalettes?.[0]"
          @saveTheme="saveTheme"
        />
      </aha-modal>
      <aha-modal
        v-if="isShowDeleteThemeModal"
        @hide="isShowDeleteThemeModal = false"
        :title="$i18nPresenter.t('Are you sure you want to delete this theme?')"
      >
        <p v-html="deleteModalContent"></p>
        <template #action>
          <aha-antd-button icon-name="delete" variant="danger" @click="deleteTheme(removingTheme)">
            {{ $i18nPresenter.t('Delete') }}
          </aha-antd-button>
        </template>
      </aha-modal>

      <aha-modal
        v-if="isShowShareWithTeam"
        @hide="cancelToggleShareTheme"
        :title="$i18nPresenter.t('Share theme')"
      >
        <p
          v-html="$i18nPresenter.
            t('Share {themeName} to let everyone in {teamName} use it for their presentations.',
              {
                themeName: themePickup.themeName,
                teamName: currentAccount.name,
              })"
        />
        <template #action>
          <aha-antd-button
            variant="sencondary"
            @click="cancelToggleShareTheme()"
          >
            {{ $i18nPresenter.t('Cancel') }}
          </aha-antd-button>
          <aha-antd-button
            variant="primary"
            :hasMarginLeft="true"
            @click="toggleShareThemeWithTeam(true)"
          >
            {{ $i18nPresenter.t('Share') }}
          </aha-antd-button>
        </template>
      </aha-modal>
      <aha-modal
        v-if="isShowUnShareWithTeam"
        @hide="cancelToggleShareTheme"
        :title="$i18nPresenter.t('Unshare theme')"
      >
        <p
          v-html="$i18nPresenter.t('Unsharing this theme won’t affect existing presentations, but your teammates won’t be able to select this theme for new presentations.')"
        />
        <template #action>
          <aha-antd-button
            variant="sencondary"
            @click="cancelToggleShareTheme()"
          >
            {{ $i18nPresenter.t('Cancel') }}
          </aha-antd-button>
          <aha-antd-button
            variant="primary"
            :hasMarginLeft="true"
            @click="toggleShareThemeWithTeam(false)"
          >
            {{ $i18nPresenter.t('Unshare') }}
          </aha-antd-button>
        </template>
      </aha-modal>
      <aha-modal
        v-if="isShowTeamCreationSuggestionModal"
        @hide="closeTeamCreationSuggestionModal"
        :title="$i18nPresenter.t('Want to share this theme?')"
      >
        <p
          v-html="$i18nPresenter.t('Create a team to collaborate and manage themes together.')"
        />
        <template #action>
          <aha-antd-button
            variant="text-link"
            size="sm"
            @click="closeTeamCreationSuggestionModal()"
            name="CloseTeamCreationSuggestionModal"
            v-aha-emit-action
          >
            {{ $i18nPresenter.t('Later') }}
          </aha-antd-button>
          <aha-antd-button
            name="theme-learn-more-team"
            v-aha-emit-action
            class="ml-1"
            size="sm"
            tabindex="-1"
            variant="link"
          >
            <a
              class="leading-none flex items-center"
              target="_blank"
              href="https://help.ahaslides.com/portal/en/kb/articles/managing-your-team-members"
            >
              <span>
                {{ $i18nPresenter.t('Explore Team') }}
              </span>
              <aha-icon
                class="ml-1"
                width="12"
                viewBox="0 0 12 12"
                name="thin-open-window"
              />
            </a>
          </aha-antd-button>
        </template>
      </aha-modal>
      <aha-modal
        v-if="applyingTheme"
        @hide="applyingTheme = null"
        :title="$i18nPresenter.t('Before applying theme')"
      >
        <p class="mb-4">
          {{ $i18nPresenter.t('Apply_theme_content_1') }}
        </p>
        <p>
          {{
            $i18nPresenter.t('Apply_theme_content_2')
          }}
        </p>
        <template #action>
          <div class="flex justify-between flex-1 items-center">
            <aha-antd-button
              name="theme-learn-more-apply-theme"
              v-aha-emit-action
              class="transform -translate-x-2"
              size="sm"
              tabindex="-1"
              variant="link"
            >
              <a
                class="leading-none flex items-center"
                target="_blank"
                href="https://help.ahaslides.com/portal/en/kb/articles/design-your-background-on-ahaslides"
              >
                <span>
                  {{ $i18nPresenter.t('Learn more') }}
                </span>
                <aha-icon
                  class="ml-1"
                  width="12"
                  viewBox="0 0 12 12"
                  name="thin-open-window"
                />
              </a>
            </aha-antd-button>
            <div class="flex">
              <aha-antd-button
                variant="sencondary"
                @click="applyingTheme = null"
                name="closeConfirmApplyThemeModal"
                v-aha-emit-action
              >
                {{ $i18nPresenter.t('Cancel') }}
              </aha-antd-button>
              <aha-antd-button
                variant="primary"
                class="ml-2"
                @click="() => {
                  setApliedThemeFlag()
                  setTheme(applyingTheme);
                  applyingTheme = null;
                }"
                name="confirmApplyThemeModal"
                v-aha-emit-action
              >
                {{ $i18nPresenter.t('Apply theme') }}
              </aha-antd-button>
            </div>
          </div>
        </template>
      </aha-modal>
    </section>
  </section>
</template>

<script>
import {
  mapActions, mapGetters, mapState, mapMutations,
} from 'vuex'
import { omit } from 'lodash'
import * as Sentry from '@sentry/vue'
import DesignEditPanel from '@/components/presentations/types/DesignEditPanel.vue'
import ThemeConfig from '@/components-v2/presentation-editor/side-right/right-nav/ThemeConfig.vue'
import ThemeThumbnail from '@/components-v2/presentation-editor/side-right/right-nav/ThemeThumbnail.vue'
import { getWithExpiry, setWithExpiry, DAY_IN_MILLISECONDS } from '@/utils/local.storage.expiry'
import EventBus from '@/eventBus'
import Utils from '@/util'

const APPLYING_THEME_LOCAL_STORAGE_KEY = 'apply-theme'

export default {
  name: 'ThemeSetting',
  components: {
    ThemeConfig,
    ThemeThumbnail,
  },
  extends: DesignEditPanel,
  data() {
    return {
      isShowConfigModal: false,
      removingTheme: null,
      applyingTheme: null,
      themePickup: null,
      isShowDeleteThemeModal: false,
      isShowShareWithTeam: false,
      isShowUnShareWithTeam: false,
      isShowTeamCreationSuggestionModal: false,
      // For Preview Theme Logo
      tempPresentationLogo: null,
      tempColorPalette: null,
    }
  },
  computed: {
    ...mapGetters('themeModule', ['themeList', 'officialThemes', 'teamThemes',
      'currentPresentationTheme', 'canCreateTheme', 'canViewThemeGallery']),
    ...mapGetters('presetModule', ['presetColorPalettes']),
    ...mapGetters('presentationAttributeModule', ['presentationAttributeColorPalette']),
    ...mapGetters(['isWhiteLabel']),
    ...mapGetters('slideModule', ['hasCustomisedSlide']),
    ...mapState({
      presentation: (state) => state.presentation,
      slideActive: (state) => state.slideActive,
      currentAccount: (state) => state.currentAccount,
      currentUser: (state) => state.currentUser,
    }),
    applyingThemeUserKey() {
      return `${this.currentUser.id}-${APPLYING_THEME_LOCAL_STORAGE_KEY}`
    },
    deleteModalContent() {
      const isShare = this.removingTheme?.isShareWithTeam
      const isOfficial = this.removingTheme?.isOfficial
      const themeName = `"${this.removingTheme?.themeName}"`
      if (isShare && isOfficial) {
        return this.$i18nPresenter.t('deleteOfficalAndSharedTheme', { themeName })
      }

      if (isShare) {
        return this.$i18nPresenter.t('deleteSharedThemeMessage', { themeName })
      }

      if (isOfficial) {
        return this.$i18nPresenter.t('deleteOfficialTheme', { themeName })
      }

      return themeName
    },
    showSectionTeamThemes() {
      return this.teamThemes.length > 0
    },
  },
  methods: {
    ...mapActions('themeModule', ['setThemeAction', 'createThemesAction', 'deleteThemeAction',
      'toggleShareThemeAction', 'setPreviewThemeAction']),
    ...mapActions(['updatePresentationAction', 'updateSlideActiveAction']),
    ...mapMutations('presentationAttributeModule', ['updatePresentationAttribute']),
    ...mapMutations('themeModule', ['setIsCreating']),
    ...mapActions('history', ['setSuppressHistoryAction']),
    _getColorPaletteFrom(theme) {
      if (!this.presetColorPalettes || !theme.colorPalette) return null
      return theme.colorPalette
    },
    async _uploadFile(file) {
      const formData = this.createUploadFormData({
        file,
        accessCode: this.presentation.uniqueAccessCode,
      })

      return this.handleUploadCroppedImage(formData)
    },
    async _uploadThemeAssets(theme) {
      const { logo, backgroundImage } = theme
      const logoTask = logo ? this._uploadFile(logo) : Promise.resolve()
      const backgroundImageTask = backgroundImage ? this._uploadFile(backgroundImage) : Promise.resolve()
      return Promise.all([logoTask, backgroundImageTask])
    },
    async setApliedThemeFlag() {
      const hadClickedApplyTheme = getWithExpiry(this.applyingThemeUserKey)
      if (hadClickedApplyTheme) return
      setWithExpiry(this.applyingThemeUserKey, true, 1 * DAY_IN_MILLISECONDS)
    },
    async handleSelectTheme(theme) {
      const isSameTheme = theme?.id === this.currentPresentationTheme?.themeId
      if (isSameTheme) return

      const hadClickedApplyTheme = getWithExpiry(this.applyingThemeUserKey)
      if (!this.hasCustomisedSlide || hadClickedApplyTheme) {
        this.setTheme(theme)
        return
      }

      // Show Confirm Modal
      this.applyingTheme = theme
    },
    async applyTheme(item, selectedSlides) {
      if (this.canViewThemeGallery) return
      // eslint-disable-next-line prefer-rest-params
      this.selectTheme(item, selectedSlides)
    },
    async setTheme(theme) {
      const allSlides = this.presentation.slides
      this.applyTheme({
        ...this.currentPresentationTheme,
        ...theme,
      }, allSlides)
      this.setFontFromTheme(theme)
      this.setLogoFromTheme(theme)
      this.setColorPalleteFromTheme(theme)
      this.tempColorPalette = null
      this.setThemeAction({
        presentationId: this.presentation.id,
        ...theme,
        colorPalette: this._getColorPaletteFrom(theme),
        themeId: theme.id,
      })
      this.refreshActiveSlide()
    },
    async refreshActiveSlide() {
      this.setSuppressHistoryAction(true)
      // This is a hack to refresh the active slides
      // because the active slide is not updated when the theme is changed
      const slideActive = JSON.parse(JSON.stringify(this.slideActive))
      this.updateSlideActiveAction({ type: 'freeStyle' })
      await this.$nextTick()
      this.updateSlideActiveAction({
        type: slideActive.type,
      })
      this.setSuppressHistoryAction(false)
    },

    async setColorPalleteFromTheme(theme) {
      if (!theme.colorPalette) return
      this.updatePresentationAttribute({
        attributeName: 'colorPalette',
        attributeValue: this._getColorPaletteFrom(theme),
      })
    },
    async setFontFromTheme(theme) {
      this.setFontFamily(theme.fontFamily)
      this.updatePresentationAction({
        fontFamily: theme.fontFamily,
      })
    },
    async setLogoFromTheme(theme) {
      if (!theme.logo) return
      this.updatePresentationAction({
        logo: theme.logo,
        notShowAhaSlidesLogo: false,
      })
      // Remove the logo from the preview theme cause it's already applied
      this.tempPresentationLogo = null
    },
    async setPreviewTheme(theme, type, selectedSlides) {
      const omittedIdTheme = omit(theme, 'id')
      if (this.canViewThemeGallery) {
        this.setPreviewThemeAction(omittedIdTheme)
        // Because the state of the CanvasTransitionBackgroundImage.vue is not computed props (don't know why we did this in the past)
        // I hate to use this, But this is how we can update the state of the canvas slide Active
        EventBus.$emit('eventHandleSetValueTransition')
        return
      }
      this.handleHoverOnTheme(omittedIdTheme, type, selectedSlides)
    },
    async unsetPreviewTheme(theme, type, selectedSlides) {
      const omittedIdTheme = omit(theme, 'id')
      if (this.canViewThemeGallery) {
        this.setPreviewThemeAction(null)
        // Because the state of the CanvasTransitionBackgroundImage.vue is not computed props (don't know why we did this in the past)
        // I hate to use this, But this is how we can update the state of the canvas slide Active
        EventBus.$emit('eventHandleSetValueTransition')
        return
      }
      this.handleHoverOnTheme(omittedIdTheme, type, selectedSlides)
    },
    async previewTheme(theme) {
      const allSlides = this.presentation.slides
      const previewTheme = {
        ...this.currentPresentationTheme,
        ...theme,
      }
      this.setPreviewTheme(previewTheme, 'over', allSlides)

      // Logo
      this.tempPresentationLogo = {
        logo: this.presentation.logo,
        notShowAhaSlidesLogo: this.presentation.notShowAhaSlidesLogo,
      }
      if (theme.logo) {
        this.updatePresentationAction({
          logo: theme.logo,
          notShowAhaSlidesLogo: false,
        })
      }

      this.tempColorPalette = this.presentationAttributeColorPalette
      this.setColorPalleteFromTheme(theme)

      this.testFontFamily(theme.fontFamily)
      this.refreshActiveSlide()
    },
    async removePreview(theme) {
      const allSlides = this.presentation.slides
      const previewTheme = {
        ...this.currentPresentationTheme,
        ...theme,
      }
      this.unsetPreviewTheme(previewTheme, 'out', allSlides)

      if (this.tempPresentationLogo) {
        this.updatePresentationAction({
          ...this.tempPresentationLogo,
        })
      }

      this.setColorPalleteFromTheme({
        colorPalette: this.tempColorPalette,
      })
      this.testFontFamily(this.presentation.fontFamily)
      this.refreshActiveSlide()
    },
    async saveTheme(theme) {
      try {
        this.setIsCreating(true)
        const [logoResponse, backgroundImageResponse] = await this._uploadThemeAssets(theme)
        await this.createThemesAction({
          ...theme,
          logo: logoResponse?.body?.file?.key,
          backgroundImage: backgroundImageResponse?.body?.file?.location,
        })
        this.setIsCreating(false)
        this.isShowConfigModal = false
        Utils.showToastSuccess(this.$i18nPresenter.t('New theme saved'))
      } catch (error) {
        Sentry.captureMessage(new Error('Create Theme Error'), {
          level: 'warning',
          extra: {
            error,
            theme,
          },
        })
        this.setIsCreating(false)
        this.isShowConfigModal = false
        Utils.showToastError(this.$i18nPresenter.t('Theme creation failed. Please try again in a moment.'))
      }
    },
    async showDeleteThemeModal(theme) {
      this.removingTheme = theme
      this.isShowDeleteThemeModal = true
    },
    async deleteTheme(theme) {
      this.isShowDeleteThemeModal = false
      await this.deleteThemeAction(theme.id)
      Utils.showToastSuccess(this.$i18nPresenter.t('Theme deleted'))
    },
    toggleShareThemeModal(theme, isShare) {
      if (!this.currentAccount.id) {
        this.isShowTeamCreationSuggestionModal = true
        return
      }
      this.themePickup = theme
      this.isShowShareWithTeam = isShare
      this.isShowUnShareWithTeam = !isShare
    },
    cancelToggleShareTheme() {
      this.isShowShareWithTeam = false
      this.isShowUnShareWithTeam = false
    },
    async toggleShareThemeWithTeam(isShare) {
      this.isShowShareWithTeam = false
      this.isShowUnShareWithTeam = false

      await this.toggleShareThemeAction({ id: this.themePickup.id, isShare })

      Utils.showToastSuccess(
        this.$i18nPresenter.t(isShare ? 'Theme shared successfully!' : 'Theme has been unshared!'),
      )
    },
    closeTeamCreationSuggestionModal() {
      this.isShowTeamCreationSuggestionModal = false
    },
  },
}
</script>
